var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class BlustreamAcm200 {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.statusRegExps = {
            firmwareVersion: /^\s*FW Version:\s+([\d+\.]*)/i,
            inputHeading: /^\s*In\s+EDID\s+IP/i,
            inputRoute: /^\s*(\d+)\s+(.+)\s+([\d\.]+)/i,
            lanHeading: /^\s*LAN\s+DHCP\s+IP\s+Gateway\s+SubnetMask/i,
            outputHeading: /^\s*Out\s+FromIn\s+IP/i,
            outputRoute: /^\s*(\d+)\s+(\d+)\s+/i,
        };
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                return {
                    videoInputs: (_b = (_a = this.statusInfo) === null || _a === void 0 ? void 0 : _a.inputs) !== null && _b !== void 0 ? _b : [],
                    videoOutputs: (_d = (_c = this.statusInfo) === null || _c === void 0 ? void 0 : _c.outputs) !== null && _d !== void 0 ? _d : [],
                };
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _e, _f;
                return (_f = (_e = this.statusInfo) === null || _e === void 0 ? void 0 : _e.firmwareVersion) !== null && _f !== void 0 ? _f : 'Unknown';
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _g, _h;
                const route = (_g = this.statusInfo) === null || _g === void 0 ? void 0 : _g.routes[key];
                if (route === undefined) {
                    this.log.error(`no route for ${key} in ${JSON.stringify((_h = this.statusInfo) === null || _h === void 0 ? void 0 : _h.routes)}`);
                    return '001';
                }
                return route;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`OUT${key.padStart(3, '0')}FR${value.padStart(3, '0')}`, /Set output (\d+) from input (\d+)|\[ERROR\]/i, tags);
                if (match[2]) {
                    return match[2].padStart(3, '0');
                }
                else {
                    return this.state.videoMatrixRoutes.values[key];
                }
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                function: () => __awaiter(this, void 0, void 0, function* () { return yield this.queryStatus(['status', 'poll'], true); }),
            },
        ];
    }
    // phase callbacks
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.queryStatus(['initialize'], false);
                return 'connected';
            }
            catch (error) {
                return 'verifyFailed';
            }
        });
    }
    // other class methods
    parseStatusResponse(data) {
        const lines = data.split(/[\r\n]+/);
        let parserState = 'firmwareVersion';
        let firmwareVersion = 'Unknown';
        const inputs = [];
        const outputs = [];
        const routes = {};
        for (const line of lines) {
            if (parserState === 'firmwareVersion') {
                const match = this.statusRegExps.firmwareVersion.exec(line);
                if (!match)
                    continue;
                firmwareVersion = match[1];
                parserState = 'inputs';
            }
            else if (parserState === 'inputs') {
                if (this.statusRegExps.outputHeading.test(line)) {
                    parserState = 'outputs';
                    continue;
                }
                const match = this.statusRegExps.inputRoute.exec(line);
                if (!match)
                    continue;
                inputs.push({
                    code: match[1],
                    external: true,
                    name: `Transmitter ${match[1]}`,
                });
            }
            else if (parserState === 'outputs') {
                if (this.statusRegExps.lanHeading.test(line)) {
                    break;
                }
                const match = this.statusRegExps.outputRoute.exec(line);
                if (!match)
                    continue;
                outputs.push({
                    code: match[1],
                    name: `Receiver ${match[1]}`,
                });
                routes[match[1]] = match[2];
            }
        }
        return { firmwareVersion, inputs, outputs, routes };
    }
    queryStatus(tags, update) {
        return __awaiter(this, void 0, void 0, function* () {
            const match = yield this.socket.send('STATUS', /^===+[^=]+===+/, tags);
            this.statusInfo = this.parseStatusResponse(match[0]);
            if (update) {
                this.state.firmwareVersion.update(this.statusInfo.firmwareVersion);
                this.state.detected.update({
                    videoInputs: this.statusInfo.inputs,
                    videoOutputs: this.statusInfo.outputs,
                });
                for (const [key, value] of Object.entries(this.statusInfo.routes)) {
                    this.state.videoMatrixRoutes.update(key, value);
                }
            }
        });
    }
}
// static properties
BlustreamAcm200.driverType = 'socketDriver';
BlustreamAcm200.key = '-MWimPYqWGj6a8v-3Nuu';
BlustreamAcm200.baseSocketOptions = {
    // The ACM 200 has a multi-line status response that is more convenient to handle as a
    // single "command" rather than processing line-by-line. This command extractor extracts
    // normal \r\n terminated commands as well as the multi-line status command which starts
    // and ends with a line of many "=" characters.
    commandExtractor: (buffer) => {
        let command = '';
        let numLines = 0;
        for (let i = 0; i < buffer.length; i++) {
            const c = buffer.charAt(i);
            if (c === '\r' || c === '\n') {
                if (command.length > 0) {
                    if (command.charAt(0) === '=' &&
                        (numLines === 0 || command.charAt(command.length - 1) !== '=')) {
                        command += c;
                        numLines++;
                        continue;
                    }
                    return { command, buffer: buffer.slice(i + 1) };
                }
            }
            else {
                command += c;
            }
        }
        return undefined;
    },
    sendTerminator: '\r',
};
BlustreamAcm200.socketOptions = [
    Object.assign(Object.assign({}, BlustreamAcm200.baseSocketOptions), { port: 23 }),
    Object.assign(Object.assign({}, BlustreamAcm200.baseSocketOptions), { port: 4999 }),
];
